import { useRoomContext } from '@livekit/components-react'
import { type LocalAudioTrack, type LocalTrackPublication, RoomEvent, Track } from 'livekit-client'
import { useCallback, useEffect, useRef, useState } from 'react'

import { canAutoEnableNoiseFilter, deviceClassLabel } from './deviceClass'
import { report } from './diagnostics'
import { krispAllowed } from './noiseFilterGate'

type Credential = { code?: string | null; reconnectToken?: string | null }

/**
 * Noise cancellation for the microphone, role-split
 *
 * - STUDENTS get the opt-in pill with the LOCAL RNNoise WASM path only,
 *   regardless of provider. Krisp is NEVER instantiated for a student:
 *   on LiveKit Cloud, Krisp voice isolation is BILLED PER MINUTE of processor
 *   runtime ($0.0012/min per the LiveKit contract), and the product decision
 *   is NO paid noise cancellation — see the krispCapable derivation below.
 *   RNNoise runs entirely in the browser (same-origin 152 KB wasm), so it is
 *   free on Cloud and self-host alike. Manual opt-in only; a stored choice is
 *   honoured, and the default stays off (canAutoEnableNoiseFilter, below).
 * - teachers keep their current behavior: the provider-derived flow (Cloud →
 *   Krisp, self-host → RNNoise) which the current policy switches off
 *   entirely (see noiseFilterAvailable).
 *
 * (When Krisp DOES engage, the package itself turns the browser's built-in
 * noiseSuppression off on the capture track, so the two never stack; RNNoise
 * runs behind the same rule.)
 *
 * CAPABILITY (MEET-NC-FIX-01, live behaviour): Krisp is a LiveKit CLOUD
 * feature. The package's `setEnabled` makes a client fetch to
 * `{serverUrl}/settings` to read the noise-cancellation entitlement from a
 * `features` object (there is no KrispOptions flag to skip the call). On the
 * hosted LiveKit Cloud service that endpoint serves the entitlement. On
 * self-hosted LiveKit servers — including our `wss://rtc.everhall.example` —
 * the endpoint is not served (returns 404 unauth, 401 with a bearer), so
 * every enable lands on a krisp=enable-timeout/failed after our 25 s
 * deadline and the teacher is invited to retry (which fails the same way,
 * forever). The fix: capability is derived from the session server URL —
 * `isLiveKitCloudServer(serverUrl)` — and when false, the entire Krisp code path
 * is short-circuited at the top of the hook: no module dynamic import, no
 * setProcessor, no setEnabled, no /settings fetch, no "Starting…" spinner,
 * no "tap to try again" loop. Status is `unavailable` from the start, which
 * the ControlBar treats the same as `unsupported` (browser cannot do it) and
 * hides the pill entirely. The browser's standard `noiseSuppression` from
 * `audioCaptureDefaults` stays on as the always-on fallback so the mic still
 * gets basic NS. The current teacher policy disables both the Cloud Krisp and
 * self-hosted RNNoise paths, including any stored preference.
 *
 * The ~6 MB module (WASM embedded; NC models fetched at runtime from
 * integrations.livekit.io — see the CSP note in deploy/nginx.conf) is
 * dynamic-imported ONLY when a Host with the feature enabled reaches the
 * room. It never touches the join path, and students never download it.
 *
 * Lifecycle follows the official useKrispNoiseFilter pattern (GROK-KRISP B2):
 * the processor stays ATTACHED for the life of its track and the toggle is
 * `setEnabled(false/true)` — `stopProcessor` destroys the Krisp instance
 * permanently (a disposed filter silently refuses every later enable), so it
 * runs only on unmount, and a republished mic gets a FRESH filter instance
 * (livekit-client stops the old track on unpublish, which destroys the
 * processor riding it). Device switches never come through here at all:
 * livekit-client restarts the attached processor in place.
 *
 * Failure is silent by policy: cleaned audio is an upgrade, plain audio is
 * the class. Every failure path falls back to the unprocessed track and
 * reports to telemetry rather than the teacher mid-lesson.
 *
 * `status: 'on'` is asserted only once `isEnabled()` agrees — the cloud
 * entitlement check and the engine start happen behind the filter's own
 * `setEnabled`, and a pill that glows while audio goes out raw would be a lie
 * (GROK-KRISP H1). But that check is POLLED, never awaited: see
 * waitUntilRunning for why awaiting it wedged the pill on "Starting…".
 */

/**
 * Capability check: is the session's room server a LiveKit Cloud host?
 *
 * LiveKit Cloud serves under `*.livekit.cloud` (the public Cloud) and
 * `*.livekit.run` (the self-hosted Cloud option). LiveKit CLOUD is the only
 * environment that serves the `/settings` entitlement endpoint the Krisp
 * package calls in `setEnabled`. A non-Cloud URL (e.g. our
 * `wss://rtc.everhall.example`) → false. Malformed input → false (the safe
 * direction: a malformed URL must not cause us to make a /settings request
 * we cannot service).
 */
const STORE_KEY = 'everhall-meet:krisp'
/**
 * How long we wait for the pipeline to REPORT itself running before telling
 * the teacher it did not.
 *
 * Note what this deadline does and does not cover: the model download and
 * engine init happen inside `setProcessor()`, which we await BEFORE any of
 * this — so these budgets cover the cloud entitlement check and the engine
 * start, not the ~6 MB fetch. The cold path gets the longer one anyway,
 * because that is where the entitlement round-trip happens on a Cairo link
 * and a pill stuck on "Starting…" is worse than one that offers a retry.
 */
const ENABLE_TIMEOUT_COLD_MS = 25_000
const ENABLE_TIMEOUT_WARM_MS = 15_000
const ENABLE_POLL_MS = 250

/**
 * How many dropped buffers before we stop cleaning this teacher's audio.
 *
 * A buffer drop means the CPU could not keep up and audio was flushed — the
 * teacher's voice glitches, and every child in the class hears it. The count was
 * already reported as "the canary if the CPU is not affordable", and then nothing
 * watched the canary: a machine that could not run the filter glitched its way
 * through the entire lesson with the pill cheerfully showing "On".
 *
 * Set where a run of drops is unambiguous rather than at the first one: a single
 * drop happens on any machine when something else briefly steals the CPU, and
 * disabling on one would take noise cancellation away from teachers who had it
 * working fine. Sustained drops mean this machine cannot do this, and clean audio
 * was never worth broken audio.
 */
const MAX_BUFFER_DROPS = 5

/**
 * MEET-NC-FIX-02 (FIX 5) — on the AudioContext.statechange path, the
 * OLD code counted every suspended/interrupted transition as a "buffer
 * drop" and persisted the NC preference off. That was wrong: the
 * context goes suspended on ordinary tab-background, phone call, Siri,
 * audio focus, OS sleep — none of which are CPU starvation. The fix
 * is to call ctx.resume() (idempotent, what livekit-client does) on
 * every state change, and only escalate to track.stopProcessor() if
 * resume() itself fails REPEATEDLY. This threshold is the count of
 * consecutive resume() rejections before the escalation. Three is
 * chosen to ride out a transient autoplay policy / system audio
 * glitch; on the 4th, the user has clearly lost the audio path
 * and the right move is to fall back to raw mic. The persisted
 * preference is NEVER written from this automatic path — a stuck
 * suspend on a single lesson must not turn NC off forever.
 */
const MAX_RNNOISE_RESUME_FAILURES = 3

/**
 * Waits for the filter to REPORT itself running, polling rather than awaiting.
 *
 * `setEnabled` takes the filter's internal mutex, and livekit-client's own
 * `onPublish` hook already calls `setEnabled(true)` — holding that mutex while
 * it checks the cloud entitlement and loads models. Awaiting our own
 * `setEnabled` therefore queues behind a promise we do not own, with no
 * deadline: when that inner call was slow (or never settled), the pill sat on
 * "Starting…" for the rest of the lesson. `isEnabled()` is a synchronous read
 * of the same ground truth, so poll it and own the clock.
 *
 * MEET-NC-FIX-02 (FIX 4): for RNNoise, `isEnabled()` returning true
 * does NOT mean audio is actually flowing — the AudioContext can be
 * in 'suspended' state (autoplay policy, tab background, audio
 * focus). The engage code uses this helper to gate status:on on the
 * combined check (engine state + AudioContext.state), so a green
 * pill never means a silent mic. The statechange listener attached
 * in engage keeps the context running once the autoplay policy
 * relents — but we must not claim 'on' until it actually is running
 * right now.
 */
function isFilterRunning(filter: NoiseFilter): boolean {
  if (!filter.isEnabled()) return false
  // RNNoise: also confirm the AudioContext is running. The Krisp
  // filter has no audioContext getter; for Krisp, isEnabled() alone
  // is the ground truth (the engine's own running state). The cast
  // is needed because the union type does not carry audioContext on
  // the Krisp branch; the `in` check is the actual discriminator.
  if ('audioContext' in filter) {
    return (filter as RnnoiseFilter).audioContext?.state === 'running'
  }
  return true
}

async function waitUntilRunning(
  filter: NoiseFilter,
  deadlineMs: number,
  cancelled: () => boolean,
): Promise<boolean> {
  const deadline = Date.now() + deadlineMs
  while (Date.now() < deadline) {
    if (cancelled()) return false // superseded: stop burning timers on a dead run
    if (isFilterRunning(filter)) return true
    await new Promise((resolve) => window.setTimeout(resolve, ENABLE_POLL_MS))
  }
  return isFilterRunning(filter)
}

export type NoiseFilterStatus =
  | 'off'
  | 'loading'
  | 'on'
  | 'unsupported'
  | 'failed'
  // MEET-NC-FIX-01: the session's room server is not a LiveKit Cloud host,
  // so the Krisp /settings entitlement endpoint is not served. The whole
  // Krisp code path is short-circuited; the ControlBar hides the pill the
  // same way it does for 'unsupported' (browser cannot do it). Browser
  // noiseSuppression is the fallback.
  | 'unavailable'

export interface NoiseFilterApi {
  /** UI state for the teacher toolbar pill. */
  status: NoiseFilterStatus
  /** The persisted preference (may be true while status is unsupported). */
  enabled: boolean
  toggle: () => void
}

/**
 * Tri-state, and it has to be: `'1'` on, `'0'` off, ABSENT means "never chose".
 *
 * It used to be two-state — absent meant on, because on was the unconditional
 * default. Now that the default is conditional (see below), "on" and "never
 * chose" have to be told apart: storing on as an absent key would mean a teacher
 * who deliberately switched noise cancellation on, on a machine whose default is
 * off, arrived next lesson with it off again and no way to make it stick.
 *
 * Reading is backwards compatible — an existing `'0'` still means off, and every
 * teacher who never touched the toggle simply falls through to the capability
 * default, which is where they should have been all along.
 */
function loadEnabled(): boolean {
  try {
    const stored = localStorage.getItem(STORE_KEY)
    if (stored === '0') return false
    if (stored === '1') return true
    // No choice on record. Krisp is a WASM audio pipeline running our heaviest
    // model on the one microphone that IS the lesson, and when it runs out of
    // CPU it drops buffers — a glitch every child hears. On the 4 GB Celeron
    // laptops our teachers teach from, defaulting it on unasked trades the audio
    // we cannot afford to lose for noise reduction nobody requested. One tap
    // still turns it on, and now that choice persists.
    return canAutoEnableNoiseFilter()
  } catch {
    return canAutoEnableNoiseFilter()
  }
}

function saveEnabled(on: boolean): void {
  try {
    localStorage.setItem(STORE_KEY, on ? '1' : '0')
  } catch {
    /* private mode — the preference just does not persist */
  }
}

type KrispModule = typeof import('@livekit/krisp-noise-filter')
type KrispFilter = ReturnType<KrispModule['KrispNoiseFilter']>
type RnnoiseModule = typeof import('./noiseSuppression/rnnoiseProcessor')
/** Instance type of the class exported by the rnnoiseProcessor module.
 *  `typeof Module['Class']` is the constructor; we need the instance
 *  shape (so the NoiseFilter union sees the prototype methods). */
type RnnoiseFilter = InstanceType<RnnoiseModule['RnnoiseProcessor']>

/** The minimum surface both filters must expose for the engage/disengage
 *  flow to be uniform. setEnabled returns a Promise so the existing
 *  `void .catch(...)` and `await ?.setEnabled(false)` patterns work for
 *  both; the actual RNNoise implementation returns Promise.resolve()
 *  because its work is synchronous, but the contract matches Krisp's. */
type NoiseFilter = KrispFilter | RnnoiseFilter

export function useNoiseFilter(
  isHost: boolean,
  credential: Credential,
  /**
   * The session's room server URL (JoinPage `phase.details.server_url`,
   * passed to LiveKitRoom as `serverUrl` and to us as a prop via the
   * Classroom `context`). Used ONLY to derive capability — the hook never
   * opens its own network call to this URL; the Krisp package would do
   * that, on non-Cloud it is short-circuited here so it never gets the
   * chance.
   */
  serverUrl: string,
): NoiseFilterApi {
  const room = useRoomContext()
  // Capability is stable for a session: the room URL is in the join token,
  // not something that changes mid-lesson. The hook therefore reads it
  // once and threads it through every gate below — the engage useEffect,
  // the LocalTrackPublished useEffect, the toggle, and the initial
  // state — so a non-Cloud session is short-circuited at every entry
  // point and not just one of them.
  // STUDENTS never take the Krisp path, whatever the provider.
  // LiveKit Cloud bills Krisp voice isolation per minute of processor runtime
  // ($0.0012/min per the LiveKit contract) — instantiating the Krisp processor
  // on a Cloud room is a metered PAID feature, and the product decision is NO
  // paid noise cancellation. Students get the local RNNoise WASM path only,
  // INCLUDING on Cloud rooms (it runs entirely in the browser; no /settings
  // entitlement, no billing). Hosts keep the provider-derived behavior.
  const krispCapable = krispAllowed({ isHost, serverUrl })
  // Availability (): students get the opt-in RNNoise pill. Hosts keep
  // the current policy — canAutoEnableNoiseFilter() is false, so the whole
  // feature (Cloud Krisp AND self-host RNNoise, stored preference included)
  // stays off for hosts until that policy changes.
  const noiseFilterAvailable = !isHost || canAutoEnableNoiseFilter()
  // Do not even read a stored preference while filtering is unavailable.
  const [enabled, setEnabled] = useState(() => (noiseFilterAvailable ? loadEnabled() : false))
  const [status, setStatus] = useState<NoiseFilterStatus>(() => {
    if (!noiseFilterAvailable) return 'unavailable'
    // Start as 'unsupported' on a self-host session in a browser that
    // lacks AudioWorkletNode (Safari <14.1) — neither filter can run.
    // On a Cloud session, Krisp has its own isSupported() check inside
    // engage; we let that produce 'unsupported' from a real 'off' start.
    if (!krispCapable && typeof AudioWorkletNode === 'undefined') return 'unsupported'
    return 'off'
  })

  // The module downloads once; the FILTER is per-track (a destroyed filter is
  // permanently disposed, so a new track must mean a new instance). On
  // Cloud we lazy-load Krisp; on self-host we lazy-load the RNNoise
  // processor module. The engage branches on krispCapable and the two
  // modulePromises are mutually exclusive in practice (a session is
  // either Cloud or self-host), but holding them in separate refs makes
  // the type per site cleaner than a single `Promise<unknown>`.
  const krispModuleRef = useRef<Promise<KrispModule> | null>(null)
  const rnnoiseModuleRef = useRef<Promise<RnnoiseModule> | null>(null)
  const filterRef = useRef<NoiseFilter | null>(null)
  /** The audio track the current filter is attached to. */
  const attachedRef = useRef<LocalAudioTrack | null>(null)
  const generationRef = useRef(0)
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const statusRef = useRef(status)
  statusRef.current = status
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  /** One manual retry per failure: a second failed tap means "turn it off". */
  const failedRetryUsedRef = useRef(false)
  /**
   * Dropped buffers this session. Deliberately NOT reset when the filter is
   * re-created for a republished mic: the evidence is about the MACHINE, and
   * zeroing it on every mic cycle is how a laptop that drops a buffer on each
   * one would never reach the threshold.
   */
  const bufferDropsRef = useRef(0)
  /** Latches the auto-disable so it fires once per enable, not once per drop. */
  const autoDisabledRef = useRef(false)

  // MEET-NC-FIX-02 (FIX 5) + MEET-NC-FIX-03 (a) — the
  // AudioContext.statechange handler that resumes the NC context on
  // suspend. The ref holds BOTH the context AND the handler captured
  // at attach time, so detachRnnoiseListener() removes from the right
  // context (filterRef.current may have been reassigned to a NEW
  // processor / new context by the time detach runs — re-deriving
  // ctx from filterRef would target the wrong context and the remove
  // would be a silent no-op).
  const rnnoiseListenerRef = useRef<{ ctx: AudioContext; handler: () => void } | null>(null)

  /**
   * Detach the AudioContext.statechange handler we attached in engage.
   * Called from the engage code (defensive: clears any leftover from a
   * prior processor), from disengage (M1: RNNoise drops to raw mic,
   * the context is closed by stopProcessor's processor.destroy() so
   * the listener dies with it; the explicit remove is still the
   * cleanest), and from the unmount cleanup (defensive). Uses the
   * STORED context from the ref, not a re-derivation — the FIX-03 (a)
   * insight.
   */
  const detachRnnoiseListener = () => {
    const entry = rnnoiseListenerRef.current
    if (!entry) return
    try {
      entry.ctx.removeEventListener('statechange', entry.handler)
    } catch {
      /* noop */
    }
    rnnoiseListenerRef.current = null
  }

  /**
   * MEET-NC-FIX-04 — the single RNNoise drop-to-raw-mic teardown.
   * Every site where the processor should leave the audio graph
   * (M2 not-running gate, R1 cancel-after-attach, R2
   * waitUntilRunning timeout) calls this. The earlier M1 disengage
   * path and the M5 statechange-escalation path also fit the
   * pattern but are pre-existing correct behaviour in their own
   * code paths (disengage is krispCapable-branched and tears
   * everything; the escalation is inside the listener and clears
   * its own refs). The invariant the helper enforces: after this
   * runs, no suspendable AudioContext is in the audio path — the
   * track is raw, our processor is destroyed, our context is
   * closed. A later tab background / iOS phone call / Siri / audio
   * focus loss has nothing to suspend and the mic STAYS AUDIBLE.
   *
   * Status semantics are set at the call site, NOT in the helper —
   * 'off' for cancel / user-driven paths, 'failed' for
   * un-recoverable starts. The helper just tears down.
   */
  const dropToRawMic = () => {
    detachRnnoiseListener()
    const t = attachedRef.current
    attachedRef.current = null
    filterRef.current = null
    if (t) {
      try {
        t.stopProcessor()
      } catch {
        /* noop */
      }
    }
  }

  const micTrack = useCallback((): LocalAudioTrack | null => {
    const pub = room?.localParticipant.getTrackPublication(Track.Source.Microphone)
    const track = pub?.track
    return track && track.kind === Track.Kind.Audio ? (track as LocalAudioTrack) : null
  }, [room])

  const engage = useCallback(async () => {
    if (!noiseFilterAvailable) {
      setStatus('unavailable')
      return
    }
    // role is no longer a gate — students engage the RNNoise path.
    // Hosts cannot reach here: `enabled` is held false wherever the filter is
    // unavailable, which for hosts (current policy) is always.
    const generation = generationRef.current + 1
    generationRef.current = generation
    const current = () => generationRef.current === generation

    const track = micTrack()
    if (!track) {
      // No mic published yet; the LocalTrackPublished hook re-runs this. 'off'
      // rather than a stuck spinner (GROK-KRISP H2).
      setStatus('off')
      return
    }

    try {
      // Already attached to THIS track: the filter survives toggles; just
      // re-enable it — WITHOUT passing through 'loading'. This warm path runs
      // on every mic republish; flashing "Starting…" (and clearing the
      // explainer toast) for a sub-second re-enable read as churn. (F2 M1.)
      //
      // MEET-NC-FIX-03 (M1): guard the warm path on krispCapable. RNNoise
      // no longer has a warm-bypass path — disengage() tears the
      // processor down (track.stopProcessor) so the next engage is
      // always the cold path. After M1 the implicit `filterRef.current`
      // check already excluded RNNoise (the ref is null on a fresh
      // engage), but making the intent explicit prevents a future
      // refactor that re-introduces a warm-bypass on RNNoise and
      // re-creates the silent-mic trap. Krisp is unchanged.
      if (krispCapable && attachedRef.current === track && filterRef.current) {
        // Nudge, never await (see waitUntilRunning): re-enabling can land while
        // onPublish still holds the filter's mutex.
        void filterRef.current.setEnabled(true).catch(() => undefined)
        const running = await waitUntilRunning(
          filterRef.current,
          ENABLE_TIMEOUT_WARM_MS,
          () => !current(),
        )
        if (!current()) return
        if (!running) {
          // Fail CLOSED: the nudge may still enable late, and a pill saying
          // "Didn't start" over a filter that then starts is a lie in the
          // other direction. Best-effort disable keeps them agreeing.
          void filterRef.current.setEnabled(false).catch(() => undefined)
          setStatus('failed')
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: 'krisp=enable-refused',
          })
          return
        }
        setStatus('on')
        failedRetryUsedRef.current = false
        return
      }

      setStatus('loading')
      if (krispCapable) {
        // CLOUD PATH — Krisp. Lazy import via the krisp module ref; a
        // rejected download must clear itself (caching the rejection
        // made every later retry fail instantly — a fake retry — after
        // one flaky fetch of the 6 MB chunk on a Cairo connection).
        if (!krispModuleRef.current) {
          const attempt = import('@livekit/krisp-noise-filter')
          krispModuleRef.current = attempt
          attempt.catch(() => {
            if (krispModuleRef.current === attempt) krispModuleRef.current = null
          })
        }
        const mod = await krispModuleRef.current
        if (!current()) return

        if (!mod.isKrispNoiseFilterSupported()) {
          setStatus('unsupported')
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: 'krisp=unsupported',
          })
          return
        }

        // A NEW track always gets a NEW filter: livekit-client stops the old
        // track on unpublish/republish, and stopping a track destroys the
        // processor attached to it — a disposed Krisp instance silently refuses
        // enable forever after.
        attachedRef.current = null
        filterRef.current = mod.KrispNoiseFilter({
          // The default 'medium' model let background noise through WHILE the
          // teacher spoke (first-cohort complaint: fine in silence, weak under
          // speech). 'high' is the heaviest NC model — this runs on teacher
          // laptops only, never on student phones, so the CPU is affordable;
          // buffer-drop telemetry below is the canary if it is not.
          //
          // NOT useBVC — deliberately, and not for lack of trying: BVC (voice
          // isolation) is the model actually designed for "suppress the noise
          // that overlaps my speech", which is this complaint. Two reasons it is
          // not flipped here:
          //   1. It is separately billed on LiveKit Cloud (the client reports
          //      KRISP_BACKGROUND_VOICE_CANCELLATION usage the moment it runs),
          //      so turning it on is a cost decision, not a code decision.
          //   2. 0.4.4 carries a hardcoded device blocklist — MacBook built-in
          //      mics and many common USB mics/webcams silently fall back to the
          //      NC model, and the public API cannot tell us which happened. On
          //      a laptop fleet that is a coin-flip fix we could not verify.
          // So: strongest NC now, BVC as a costed decision with a canary later.
          quality: 'high',
          // CPU overload flushes audio (a glitch the teacher hears). Counted AND
          // acted on: past a sustained run this machine has demonstrated it cannot
          // run the filter, and plain audio beats glitching audio every time.
          onBufferDrop: () => {
            bufferDropsRef.current += 1
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: 'error',
              permission: `krisp=buffer-drop,n=${bufferDropsRef.current}`,
            })
            // `<` plus a latch, NOT `!== MAX`. Equality fired exactly once per
            // session: after auto-disabling at five, a teacher who turned Krisp back
            // on could never reach five again (the count went 6, 7, 8…) and the
            // machine glitched unprotected for the rest of the lesson. The latch is
            // what stops `<` from re-running on every drop after the first kill, and
            // `toggle` re-arms it so a deliberate re-enable still gets its own
            // safety net.
            if (bufferDropsRef.current < MAX_BUFFER_DROPS || autoDisabledRef.current) return
            autoDisabledRef.current = true
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: 'error',
              permission: `krisp=auto-disabled,${deviceClassLabel()}`,
            })
            // Persisted, so the next lesson on this machine does not start by
            // glitching its way to the same conclusion. The teacher can still turn
            // it back on; it is their machine and their call.
            saveEnabled(false)
            setEnabled(false)
          },
        })

        // Re-read the track: the first import is ~6 MB and the mic may have
        // been switched or republished while it downloaded.
        const liveTrack = micTrack()
        if (!current() || !liveTrack) {
          // Mirror the RNNoise branch's cancel handling below: the Krisp filter
          // was constructed above, so destroy the half-built instance (its WASM
          // + AudioContext) rather than abandon it to GC until the next engage
          // overwrites filterRef. The next engage builds a fresh one.
          await filterRef.current?.destroy().catch(() => undefined)
          filterRef.current = null
          if (current()) setStatus('off')
          return
        }
        await liveTrack.setProcessor(filterRef.current)
        // Bookkeeping BEFORE the generation check: the processor is on the track
        // now, and unmount cleanup finds it through attachedRef — a cancel landing
        // in this gap must not orphan it. (F2 M6.)
        attachedRef.current = liveTrack
        if (!current()) {
          // A newer intent (usually "off") superseded us mid-attach: leave the
          // filter attached but disabled — bypass audio flows untouched.
          void filterRef.current.setEnabled(false).catch(() => undefined)
          return
        }

        // setProcessor resolving does NOT mean noise cancellation is running:
        // the cloud entitlement check and the engine start live behind setEnabled,
        // which livekit-client's onPublish hook has usually already called. So
        // nudge it unawaited and then wait on isEnabled() with our own deadline —
        // awaiting setEnabled meant queueing on a mutex we do not control, which
        // is how the pill got stuck on "Starting…".
        //
        // What `setEnabled` actually does (MEET-NC-FIX-01 correction): the
        // package makes a client fetch to `{serverUrl}/settings` and reads a
        // `features` object to check the noise-cancellation entitlement. There
        // is NO KrispOptions flag to skip the call. On LiveKit Cloud the
        // endpoint returns a `features` payload that includes the NC
        // entitlement, and the in-browser WASM engine starts. On a self-hosted
        // LiveKit server (e.g. rtc.everhall.example) the endpoint is not served
        // — 404 unauth, 401 with a bearer — and the enable times out at our
        // 25 s deadline. The earlier MEET-OPT-04 note that claimed a
        // `hostname.endsWith(".livekit.cloud")` predicate routes the call off
        // the Cloud path on self-host was wrong: the predicate exists in the
        // bundled livekit-client region-detection code, but the
        // /settings fetch Krisp makes is a separate, unconditional call against
        // the configured serverUrl. The whole code path above this line is
        // only reachable on a `krispCapable` server (see the hook gate at the
        // top of `useNoiseFilter`).
        void filterRef.current.setEnabled(true).catch(() => undefined)
        const running = await waitUntilRunning(
          filterRef.current,
          ENABLE_TIMEOUT_COLD_MS,
          () => !current(),
        )
        if (!current()) return
        if (!running) {
          // Same fail-closed rule as the warm path.
          void filterRef.current.setEnabled(false).catch(() => undefined)
          setStatus('failed')
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: 'krisp=enable-timeout',
          })
          return
        }
        setStatus('on')
        failedRetryUsedRef.current = false
        report(credentialRef.current, {
          stage: 'permissions',
          outcome: 'ok',
          permission: 'krisp=on',
        })
      } else {
        // SELF-HOST PATH — RNNoise. Same generation guard, same
        // attachedRef bookkeeping, same warm-path-keep-warm via
        // setEnabled, same unmount cleanup (the track's stopProcessor
        // invokes our destroy). The differences from Krisp:
        //   - NO cloud /settings fetch. Self-host servers do not serve
        //     that endpoint, but more importantly, the package does not
        //     even import it on the self-host path: it is a Krisp-only
        //     concept and was the root cause of the MEET-NC-FIX-01 bug.
        //   - The wasm (152 KB) and the worklet (65 KB) are SAME-ORIGIN
        //     hashed assets on meet.apps.everhall.example, served by the
        //     Vite ?url subpath imports. No third-party fetch, no CSP delta.
        //   - Buffer-drop is proxied via the AudioContext 'statechange'
        //     event (no Krisp onBufferDrop equivalent). When the context
        //     goes to 'suspended' or 'interrupted' (CPU starvation or a
        //     system-level pause on mobile), we count it. After
        //     MAX_BUFFER_DROPS the same auto-disable latch as Krisp kicks
        //     in: persist-off + setEnabled(false), and the teacher can
        //     still turn it back on (their machine, their call).
        //   - waitUntilRunning is reused for uniformity; for RNNoise
        //     isEnabled() is true instantly, so the first poll iteration
        //     returns immediately and the cold path lands on 'on' the
        //     moment the processor is wired and setEnabled is called.
        try {
          // Feature-detect: RNNoise is an AudioWorkletNode. Safari <14.1
          // lacks the API entirely; in that case there is no path —
          // Krisp is Cloud-only and RNNoise needs the worklet. We land
          // on 'unsupported' so the ControlBar hides the pill (same
          // treatment as Krisp's own isKrispNoiseFilterSupported=false
          // path on the Cloud branch).
          if (typeof AudioWorkletNode === 'undefined') {
            setStatus('unsupported')
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: 'error',
              permission: 'rnnoise=unsupported',
            })
            return
          }

          // Lazy import via the rnnoise module ref; same self-cleaning
          // rejection pattern as the Krisp branch. The rnnoiseProcessor
          // module pulls in the worklet + wasm URLs at top-level via
          // Vite ?url, so the dynamic import is the only place the
          // chunk is requested and the only place the hashed asset URLs
          // are resolved.
          if (!rnnoiseModuleRef.current) {
            const attempt = import('./noiseSuppression/rnnoiseProcessor')
            rnnoiseModuleRef.current = attempt
            attempt.catch(() => {
              if (rnnoiseModuleRef.current === attempt) rnnoiseModuleRef.current = null
            })
          }
          const mod = await rnnoiseModuleRef.current
          if (!current()) return

          // Construct the processor. MEET-NC-FIX-02 (FIX 2): we do NOT
          // call init() manually here. The livekit-client setProcessor
          // path we use below owns init() — calling it twice double-
          // initialises (a fresh AudioContext, a duplicate wasm fetch,
          // a duplicate addModule), leaks the first context, and
          // stranded it on the cancel-mid-init path. The SDK's
          // ProcessorOptions is what we hand to setProcessor; the
          // ProcessorOptions in the OLD manual-init() call is gone.
          attachedRef.current = null
          filterRef.current = new mod.RnnoiseProcessor()

          // Re-read the track: setProcessor (which calls init) is
          // async, and the mic may have switched or unpublished in the
          // gap. Same as the Krisp branch above.
          const liveTrack = micTrack()
          if (!current() || !liveTrack) {
            // Constructed but never attached. FIX 2: mirror the
            // Krisp branch's cancel handling — destroy the half-built
            // processor so its AudioContext does not leak. The next
            // engage will build a fresh one.
            await filterRef.current.destroy().catch(() => undefined)
            filterRef.current = null
            if (current()) setStatus('off')
            return
          }
          try {
            // FIX 2: the SDK owns init() — setProcessor calls it for
            // us with the ProcessorOptions the SDK already holds. We
            // hand it the constructed processor and nothing else (the
            // SDK's setProcessor signature is `setProcessor(processor,
            // showProcessedStreamLocally?: boolean)` — no options arg).
            await liveTrack.setProcessor(filterRef.current)
          } catch (err) {
            // setProcessor rejected — its internal init() failed
            // (wasm fetch, addModule reject, or the SDK's own
            // capability gate). Free the half-built processor.
            await filterRef.current.destroy().catch(() => undefined)
            filterRef.current = null
            throw err
          }
          // Bookkeeping BEFORE the generation check: the processor is
          // on the track now, and unmount cleanup finds it through
          // attachedRef — a cancel landing in this gap must not
          // orphan it. Same rule as the Krisp branch above. (F2 M6.)
          attachedRef.current = liveTrack
          if (!current()) {
            // MEET-NC-FIX-04 (R1) — a newer intent (usually "off")
            // superseded us mid-attach. The OLD code called
            // setEnabled(false), leaving the processor attached with
            // our suspendable AudioContext in the audio path; later
            // tab background would silence the mic. Drop to raw mic
            // (the shared helper) — no suspendable context in the
            // path. Status stays 'off' (it was set by disengage; we
            // just don't override it here).
            dropToRawMic()
            return
          }

          // FIX 4: gate status:on on ctx.state === 'running'. The
          // processor's isEnabled() is a boolean (true once
          // setEnabled(true) runs) — it is NOT a guarantee that audio
          // is actually flowing. The default-on engage runs from a mount
          // effect with no user gesture, and even with our new
          // `await ctx.resume()` in init() the autoplay policy can leave
          // the context in 'suspended'. A green pill on a silent mic is
          // the worst possible lie — surface 'failed' (or 'off') so the
          // user can retry / re-toggle. The statechange listener below
          // will keep the context running once the autoplay policy
          // relents (or the user interacts), but until ctx.state is
          // 'running' right now, we must not claim on.
          // MEET-NC-FIX-03 (M2): the OLD code called setEnabled(false)
          // here — the processor stayed attached and the bypass
          // routed raw audio through OUR AudioContext. Our context can
          // then suspend (autoplay policy / tab background / iOS phone
          // call / Siri) and the mic goes SILENT. The fix: drop to the
          // raw track (no processor, no context in the path). The
          // teacher stays audible on the raw mic, the 'failed' status
          // tells them NC didn't start and the pill gives a retry
          // affordance. The next engage is a fresh cold path (cheap:
          // cached wasm + addModule).
          const ctx = filterRef.current.audioContext
          if (!ctx || ctx.state !== 'running') {
            // MEET-NC-FIX-04 — drop to raw mic via the shared helper.
            // The OLD code called setEnabled(false), leaving the
            // processor attached and our suspendable AudioContext in
            // the audio path; later tab background would silence
            // the mic. Status: failed (the user gets the retry
            // affordance on the pill).
            dropToRawMic()
            setStatus('failed')
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: 'error',
              permission: 'rnnoise=ctx-not-running',
            })
            return
          }

          // FIX 5: replace the buffer-drop counter with a resume +
          // escalation path. AudioContext 'suspended'/'interrupted' is
          // NOT CPU starvation — it is the autoplay policy, tab
          // background, phone call, Siri, audio focus, OS sleep. The
          // right move is to call ctx.resume() (idempotent, what
          // livekit-client does), and only escalate to track.stopProcessor
          // if resume() itself fails REPEATEDLY. The persisted NC
          // preference is NEVER written from this automatic path — a
          // stuck suspend on a single lesson must not turn NC off for
          // every future lesson.
          detachRnnoiseListener() // defensive: clear any leftover from a prior processor
          let resumeFailures = 0
          const onStateChange = () => {
            if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
              ctx.resume().then(
                () => {
                  resumeFailures = 0
                },
                () => {
                  resumeFailures += 1
                  report(credentialRef.current, {
                    stage: 'permissions',
                    outcome: 'error',
                    permission: `rnnoise=resume-fail,n=${resumeFailures},state=${ctx.state}`,
                  })
                  if (resumeFailures >= MAX_RNNOISE_RESUME_FAILURES) {
                    // Auto-stop: fall back to raw mic. The user can
                    // re-engage later (the persisted preference is
                    // preserved). Detach the listener first so it
                    // does not fire on the closing context; clear
                    // filterRef so the next engage is the cold path.
                    detachRnnoiseListener()
                    const t = attachedRef.current
                    filterRef.current = null
                    if (t) {
                      try {
                        t.stopProcessor()
                      } catch {
                        /* noop */
                      }
                    }
                    setStatus('off')
                    report(credentialRef.current, {
                      stage: 'permissions',
                      outcome: 'error',
                      permission: 'rnnoise=resume-exhausted',
                    })
                  }
                },
              )
            } else if (ctx.state === 'running') {
              resumeFailures = 0
            }
          }
          // MEET-NC-FIX-03 (a) — store BOTH the context AND the
          // handler. detachRnnoiseListener removes from this stored
          // context (not from filterRef.current which may point to a
          // different processor by the time detach runs).
          rnnoiseListenerRef.current = { ctx, handler: onStateChange }
          ctx.addEventListener('statechange', onStateChange)

          // setEnabled(true) on RNNoise returns Promise.resolve()
          // (the work is synchronous — the AudioContext was wired
          // during init, setEnabled just connects the source to it).
          // We still call waitUntilRunning for uniformity with the
          // Krisp branch: isEnabled() returns true on the first poll
          // iteration, so this is essentially instant.
          void filterRef.current.setEnabled(true).catch(() => undefined)
          const running = await waitUntilRunning(
            filterRef.current,
            ENABLE_TIMEOUT_COLD_MS,
            () => !current(),
          )
          if (!current()) return
          if (!running) {
            // MEET-NC-FIX-04 (R2) — same shape as M2: the OLD code
            // called setEnabled(false), leaving the processor in a
            // silent bypass. Drop to raw mic via the shared helper.
            // Status: failed (RNNoise-specific timeout — the
            // waitUntilRunning poll did not see ctx.state === 'running').
            dropToRawMic()
            setStatus('failed')
            report(credentialRef.current, {
              stage: 'permissions',
              outcome: 'error',
              permission: 'rnnoise=enable-timeout',
            })
            return
          }
          setStatus('on')
          failedRetryUsedRef.current = false
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'ok',
            permission: 'rnnoise=on',
          })
        } catch {
          if (!current()) return
          setStatus('failed')
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'error',
            permission: 'rnnoise=failed',
          })
        }
      }
    } catch {
      if (!current()) return
      // Plain audio is the class; cleaned audio was only ever an upgrade.
      setStatus('failed')
      report(credentialRef.current, {
        stage: 'permissions',
        outcome: 'error',
        permission: 'krisp=failed',
      })
    }
  }, [room, isHost, micTrack, noiseFilterAvailable])

  const disengage = useCallback(async () => {
    if (!noiseFilterAvailable) {
      generationRef.current += 1
      setStatus('unavailable')
      return
    }
    generationRef.current += 1 // cancel any in-flight engage
    setStatus('off')
    // Always remove the statechange listener on bypass — its resume
    // / escalate path is irrelevant once the user is taking NC off.
    detachRnnoiseListener()
    if (krispCapable) {
      // Krisp (Cloud): warm bypass. The filter survives to be
      // re-enabled — the warm path in engage() reuses the same
      // instance. Krisp's onBufferDrop + the engine's isEnabled()
      // are the real ground truth; no AudioContext to suspend.
      try {
        await filterRef.current?.setEnabled(false)
      } catch {
        /* worst case the filter keeps cleaning — audio still flows */
      }
    } else {
      // RNNoise (self-host) — MEET-NC-FIX-03 (M1): drop to the raw
      // mic. The OLD code did setEnabled(false), which kept the
      // processor ATTACHED and routed raw audio through OUR
      // AudioContext. Our context can then suspend (autoplay policy,
      // tab background, iOS phone call, Siri, OS sleep) and the mic
      // goes SILENT with no resume path on the bypass. The fix:
      // tear down the processor entirely so no suspendable context
      // is in the audio path. stopProcessor invokes our
      // processor.destroy() (which closes the context, frees the
      // WASM heap via node.destroy()). The next engage is a fresh
      // cold path — cheap for RNNoise (the wasm is cached, only
      // addModule + a new AudioContext re-run), and the alternative
      // (warm bypass through a suspendable context) is the silent-
      // mic trap we are removing.
      const t = attachedRef.current
      attachedRef.current = null
      filterRef.current = null
      if (t) {
        try {
          t.stopProcessor()
        } catch {
          /* noop */
        }
      }
    }
  }, [noiseFilterAvailable, krispCapable])

  // Engage/disengage on preference or room availability. MEET-NC-BUILD-01:
  // the `!krispCapable` gate is gone — on self-host, engage() now
  // dispatches to the RNNoise branch in the cold path. The branch
  // itself is the capability gate: if AudioWorkletNode is missing on
  // self-host, it lands on 'unsupported' and the ControlBar hides the
  // pill; on Cloud the Krisp flow has its own isKrispNoiseFilterSupported
  // check.
  useEffect(() => {
    if (!room) return
    if (enabled) void engage()
    else void disengage()
  }, [room, enabled, engage, disengage])

  // A republished microphone (join-time publish, setMicrophoneEnabled cycle,
  // reconnect republishAllTracks) is a NEW track that has never seen a
  // processor. Device SWITCHES never fire this — livekit-client restarts the
  // attached processor in place — and if they did, the same-track early path
  // in engage() makes it a no-op. MEET-NC-BUILD-01: same gate change as the
  // engage useEffect above — self-host republishes fire onPublished too.
  useEffect(() => {
    if (!room) return
    const onPublished = (pub: LocalTrackPublication) => {
      if (pub.source !== Track.Source.Microphone) return
      if (enabled) void engage()
    }
    room.on(RoomEvent.LocalTrackPublished, onPublished)
    return () => {
      room.off(RoomEvent.LocalTrackPublished, onPublished)
    }
  }, [room, enabled, engage])

  // Leaving the room is the ONE place the processor is truly destroyed.
  useEffect(
    () => () => {
      generationRef.current += 1
      // FIX 5: explicit detach on teardown. The context is closed by
      // stopProcessor's processor.destroy() call, so the listener dies
      // with it; this is defensive in case the path changes and the
      // ref still holds a handler against a closed context.
      detachRnnoiseListener()
      const track = attachedRef.current
      attachedRef.current = null
      filterRef.current = null
      if (track) {
        track.stopProcessor().catch(() => undefined)
      }
    },
    [],
  )

  const toggle = useCallback(() => {
    if (!noiseFilterAvailable) {
      setStatus('unavailable')
      return
    }
    // A failed engage keeps the preference ON, and the natural tap intent on
    // "Didn't start" is TRY AGAIN, not "turn off" — so the first tap retries.
    // If that retry also fails, the next tap falls through to a normal off:
    // without this fallthrough a persistent failure would trap the teacher in a
    // retry loop with no exit. (GROK-FINAL2 M4.)
    if (statusRef.current === 'failed' && enabledRef.current && !failedRetryUsedRef.current) {
      failedRetryUsedRef.current = true
      void engage()
      return
    }
    failedRetryUsedRef.current = false
    const next = !enabledRef.current
    // Turning it back ON re-arms the buffer-drop safety net and forgets the drops
    // that killed it. The teacher has overruled our judgement, which is their right
    // — but if this machine glitches all over again it must be able to protect the
    // class a second time rather than riding out the lesson on a stale latch.
    if (next) {
      bufferDropsRef.current = 0
      autoDisabledRef.current = false
    }
    saveEnabled(next)
    setEnabled(next)
  }, [engage, noiseFilterAvailable])

  return { status, enabled, toggle }
}
